import { ServiceCallQuotationDetailsModel } from "api/models/company/service-call/service-call-quotation-details-model";
import { autoinject, computedFrom } from "aurelia-framework";
import { AgGridHelper } from "helpers/ag-grid-helper";
import { RouterHelper } from "helpers/router-helper";
import { QuotationEditParameters } from "pages/services/quotations/edit-base";
import { QuotationListBase, QuotationListParameters } from "pages/services/quotations/list-base";
import RouteRepository, { NavigationNew } from "repositories/routeRepository";
import { ServiceCallQuotationSecurityService } from "services/service-call-quotation-security-service";
import { ServiceCallQuotationService } from "services/service-call-quotation-service";
import HostContext from "core/host-context";
import { CanCloseWindow } from "interfaces/can-close-window";
import { NotificationHelper } from "helpers/notification-helper";
import { isWorker } from "cluster";
import UserAccessService from "services/user-access-service";
import { ServiceCallQuotationStatus } from "api/enums/service-call-quotation-status";
import { EventAggregator } from "aurelia-event-aggregator";
import { ServiceCallQuotationUserValidationModel } from "api/models/company/service-call/service-call-quotation-user-validation-model";
import { ViewModeHelper } from "helpers/view-mode-helper";

@autoinject()
export class QuotationListFull extends QuotationListBase implements CanCloseWindow {
    //public currentStatus: ServiceCallQuotationStatus[] = [];
    public currentStatus: Array<string | null> | null = [];
    public readonly quotationStatus: typeof ServiceCallQuotationStatus = ServiceCallQuotationStatus;
    public btnAllQuotes: HTMLTextAreaElement | null = null;

    public btnInProgressQuotes: HTMLTextAreaElement | null = null;
    public btnOnHoldQuotes: HTMLTextAreaElement | null = null;
    public btnSentToCustomerQuotes: HTMLTextAreaElement | null = null;
    public btnAcceptedByCustomerQuotes: HTMLTextAreaElement | null = null;
    public btnClosedQuotes: HTMLTextAreaElement | null = null;
    public btnAllActiveQuotes: HTMLTextAreaElement | null = null;
    public activeQuoteStatuses: Array<string | null> = [ServiceCallQuotationStatus.InProgressByTheTechnician, ServiceCallQuotationStatus.CompletedByTheTechnician, ServiceCallQuotationStatus.Entered, ServiceCallQuotationStatus.Confirmed, ServiceCallQuotationStatus.OnHold, ServiceCallQuotationStatus.SentToCustomer, ServiceCallQuotationStatus.AcceptedByTheCustomer];
    public allQuotesStatus: Array<string | null> = ["ALL"];

    public quotationGrid: any = null; // Référence à la grille pour accéder à ses méthodes

    @computedFrom("securitySettings")
    public get canCreateQuotation(): boolean {
        return this.serviceCallQuotationSecurityService.canCreateQuotation(this.securitySettings);
    }

    @computedFrom("currentStatus", "allQuotesStatus")
    public get searchRequestedHandler(): ((searchText: string) => Promise<void>) | undefined {
        // Retourner la fonction seulement si on est en mode "All Quotes"
        if (this.currentStatus && this.currentStatus.length > 0 && this.currentStatus[0] === this.allQuotesStatus[0]) {
            return this.onSearchRequested.bind(this);
        }
        return undefined;
    }

    constructor(
        serviceCallQuotationService: ServiceCallQuotationService,
        serviceCallQuotationSecurityService: ServiceCallQuotationSecurityService,
        userAccessService: UserAccessService,
        viewModeHelper: ViewModeHelper,
        private readonly eventAggregator: EventAggregator,
        private readonly routeRepository: RouteRepository,
        private readonly routerHelper: RouterHelper,
        private readonly agGridHelper: AgGridHelper,
        private readonly hostContext: HostContext,
        private readonly notificationHelper: NotificationHelper
    ) {
        super(serviceCallQuotationService, serviceCallQuotationSecurityService, userAccessService, viewModeHelper);
    }

    public async bind(): Promise<void> {
        await this.refreshWithStatus();
    }

    public async activate(params: QuotationListParameters): Promise<void> {
        await super.activate(params);
        this.currentStatus = await this.serviceCallQuotationService.getUserGridStatusSelection();
        await Promise.all([
            this.agGridHelper.lazyLoadAgGridModule()
        ]);

        (window as any).CurrentViewModel = this;
    }

    public async canActivate(params: QuotationListParameters): Promise<any> {
        const responseValidation = await this.serviceCallQuotationService.userQuotationValidation();

        if (responseValidation != null && responseValidation.ErrorId !== 0) {
            this.notificationHelper.showErrorKey(`ApiError.${responseValidation.ErrorId}`, undefined, { timeOut: 0, extendedTimeOut: 0 });
            return false;
        }
        return true;
    }

    public async refresh(): Promise<void> {
        if (this.currentStatus && this.currentStatus.length > 0 && this.currentStatus[0] === this.allQuotesStatus[0]) {
            return;
        }
        await this.refreshWithStatus();
    }

    public async onSearchRequested(searchText: string): Promise<void> {
        // Si la recherche est vide, vider la grille sans appeler le back-end
        if (!searchText || searchText.trim() === "") {
            this.quotations = [];
            this.eventAggregator.publish("ApplyFilter");
            return;
        }

        // Appeler le service avec le filtre de recherche
        this.quotations = await this.serviceCallQuotationService.getQuotations(
            null,
            this.sourceServiceCallId,
            null, // statuses = null car on est en mode "ALL"
            null,
            false,
            false,
            this.sourceWorkOrderId,
            searchText.trim()
        );

        // Rafraîchir la grille avec les nouvelles données et réappliquer les filtres sauvegardés
        this.eventAggregator.publish("ApplyFilter");
    }

    public async newChildWindow(id: number): Promise<void> {
        const quotation = await this.serviceCallQuotationService.getQuotation(id, true, true, false, true, false);
        if (quotation) {
            this.navigateToQuotation(null, quotation.IsWorkOrder, quotation.Id);
        }
    }

    public onGridRowDoubleClicked(grid: any, data: ServiceCallQuotationDetailsModel): void {
        if (data && data.Id) {
            this.navigateToQuotation(data.Id, data.IsWorkOrder, null);
        }
    }

    public async canClose(): Promise<void> {
        const childWindowsOpenCount = await (this.hostContext as any).viewModel.openChildWindowsCount();

        if (childWindowsOpenCount > 0) {
            await this.notificationHelper.showDialogOkKey("msg_SeveralQuotesStillOpen", "");
        } else {
            (this.hostContext as any).viewModel.closeWindow();
        }
    }

    public async selectedStatus(statuses: ServiceCallQuotationStatus[]): Promise<void> {
        this.currentStatus = statuses;
        // Vider la boîte de recherche
        if (this.quotationGrid && typeof this.quotationGrid.clearSearchText === "function") {
            this.quotationGrid.clearSearchText();
        }
        await this.serviceCallQuotationService.setUserGridStatusSelection(this.currentStatus);
        await this.refreshWithStatus();
    }

    private async refreshWithStatus(): Promise<void> {
        this.removeFocusClassOnBtn();
        this.addFocusOnCurrentSelectedButton();

        // Si on est en mode "ALL", relancer la recherche avec le texte actuel de la boîte de recherche ou vider la grille
        if (this.currentStatus && this.currentStatus.length > 0 && this.currentStatus[0] === this.allQuotesStatus[0]) {
            const searchText = this.quotationGrid && typeof this.quotationGrid.getSearchText === "function"
                ? this.quotationGrid.getSearchText()
                : "";
            if (searchText && searchText.trim() !== "") {
                await this.onSearchRequested(searchText);
            } else {
                // Si pas de texte de recherche, vider la grille sans appeler le back-end
                this.quotations = [];
                this.eventAggregator.publish("ApplyFilter");
            }
            return;
        }

        if (!this.currentStatus || this.currentStatus.length === 0) {
            this.quotations = await this.serviceCallQuotationService.getQuotations(null, this.sourceServiceCallId, this.activeQuoteStatuses, null, false, false, this.sourceWorkOrderId);
        } else {
            this.quotations = await this.serviceCallQuotationService.getQuotations(null, this.sourceServiceCallId, this.currentStatus, null, false, false, this.sourceWorkOrderId);
        }

        this.eventAggregator.publish("ApplyFilter");
    }

    private addFocusOnCurrentSelectedButton(): void {
        if (!this.currentStatus || this.currentStatus.length === 0 || this.currentStatus.length === this.activeQuoteStatuses.length) {
            if (this.btnAllActiveQuotes) {
                this.btnAllActiveQuotes.classList.add("focus");
            }
        }

        if (this.currentStatus![0] === this.allQuotesStatus[0]) {
            if (this.btnAllQuotes) {
                this.btnAllQuotes.classList.add("focus");
            }
        }

        if (this.currentStatus!.find((status: ServiceCallQuotationStatus) => status === ServiceCallQuotationStatus.InProgressByTheTechnician)) {
            if (this.btnInProgressQuotes) {
                this.btnInProgressQuotes.classList.add("focus");
            }
        }

        if (this.currentStatus!.find((status: ServiceCallQuotationStatus) => status === ServiceCallQuotationStatus.OnHold)) {
            if (this.btnOnHoldQuotes) {
                this.btnOnHoldQuotes.classList.add("focus");
            }
        }

        if (this.currentStatus!.find((status: ServiceCallQuotationStatus) => status === ServiceCallQuotationStatus.SentToCustomer)) {
            if (this.btnSentToCustomerQuotes) {
                this.btnSentToCustomerQuotes.classList.add("focus");
            }
        }

        if (this.currentStatus!.find((status: ServiceCallQuotationStatus) => status === ServiceCallQuotationStatus.AcceptedByTheCustomer)) {
            if (this.btnAcceptedByCustomerQuotes) {
                this.btnAcceptedByCustomerQuotes.classList.add("focus");
            }
        }

        if (this.currentStatus!.find((status: ServiceCallQuotationStatus) => status === ServiceCallQuotationStatus.CallGenerated)) {
            if (this.btnClosedQuotes) {
                this.btnClosedQuotes.classList.add("focus");
            }
        }
    }

    private removeFocusClassOnBtn(): void {
        if (this.btnAllQuotes) {
            this.btnAllQuotes.classList.remove("focus");
        }

        if (this.btnAllActiveQuotes) {
            this.btnAllActiveQuotes.classList.remove("focus");
        }

        if (this.btnInProgressQuotes) {
            this.btnInProgressQuotes.classList.remove("focus");
        }

        if (this.btnOnHoldQuotes) {
            this.btnOnHoldQuotes.classList.remove("focus");
        }

        if (this.btnSentToCustomerQuotes) {
            this.btnSentToCustomerQuotes.classList.remove("focus");
        }

        if (this.btnAcceptedByCustomerQuotes) {
            this.btnAcceptedByCustomerQuotes.classList.remove("focus");
        }

        if (this.btnClosedQuotes) {
            this.btnClosedQuotes.classList.remove("focus");
        }

        if (!this.currentStatus || this.currentStatus.length === 0 || this.currentStatus.length === this.activeQuoteStatuses.length) {
            if (this.btnAllActiveQuotes) {
                this.btnAllActiveQuotes.classList.add("focus");
            }
        }
    }

    private async initQuotations(): Promise<void> {
        this.quotations = await this.serviceCallQuotationService.getQuotations(null, this.sourceServiceCallId, null, null, false, false, this.sourceWorkOrderId);
    }

    private navigateToQuotation(quotationId: number | null, isWorkOrder: boolean, sourceQuotationId: number | null): void {
        this.routerHelper.navigateToRoute(this.routeRepository.routes.Service_Quotation_Desktop.name, { quotationId: quotationId ? quotationId : NavigationNew, isWorkOrder: isWorkOrder, sourceServiceCallId: this.sourceServiceCallId ? this.sourceServiceCallId : undefined, sourceWorkOrderId: this.sourceWorkOrderId ? this.sourceWorkOrderId : undefined, sourceQuotationId: sourceQuotationId ? sourceQuotationId : undefined} as QuotationEditParameters);
    }
}
